import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../components/loadingSpinner';
import ProjectUpload from '../components/Feature/ProjectUpload';
import Navbar from '../components/navbar';
import Modal from '../components/modal';
import GuideModal from '../components/GuideModal';
import { useUser } from '../context/UserContext';
import { useSelectedFranchisee } from '../context/SelectedFranchiseeContext';
import Swal from 'sweetalert2';

function Home() {
  const [loading, setLoading] = useState(false);
  const [isProject, setProject] = useState(true);
  const [selectedSource, setSelectedSource] = useState("ConstructConnect"); // Default to PlanHub
  const [pendingSourceChange, setPendingSourceChange] = useState(null);
  const [hasFileData, setHasFileData] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const {user} = useUser();
  const {selectedFranchisee} = useSelectedFranchisee();
  const [successCount, setSuccessCount] = useState(null);
  const [updatedCount, setUpdatedCount] = useState(null);
  const [preImportNew, setPreImportNew] = useState(null);
  const [preImportExisting, setPreImportExisting] = useState(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  // Create a combined user object with selected franchisee data
  const getUserWithSelectedFranchisee = () => {
    if (!user) return null;
    
    // If user has a selected franchisee, use that data
    if (selectedFranchisee) {
      return {
        ...user,
        franchisee: {
          id: selectedFranchisee.id,
          name: selectedFranchisee.name,
          owner: selectedFranchisee.owner,
          owner_email: selectedFranchisee.owner_email,
          hubspot_api_key: selectedFranchisee.client_type?.hubspot_api_key
        },
        hubspotID: selectedFranchisee.UserFranchiseeAssociation?.hubspotID,
        external_email: selectedFranchisee.UserFranchiseeAssociation?.external_email,
        selectedFranchisee: selectedFranchisee
      };
    }
    
    // Otherwise, return the original user object
    return user;
  };

  const userWithFranchisee = getUserWithSelectedFranchisee();

  console.log('userWithFranchisee', userWithFranchisee);

  const toggleModal = (message, success = null, updated = null, preNew = null, preExisting = null) => {
    console.log('🏠 Home.js toggleModal called with:', { message, success, updated, preNew, preExisting });
    setModalOpen(prev => !prev);
    setModalMessage(message);
    if (success !== null) setSuccessCount(success);
    if (updated !== null) setUpdatedCount(updated);
    if (preNew !== null) setPreImportNew(preNew);
    if (preExisting !== null) setPreImportExisting(preExisting);
    console.log('🏠 Home.js state after setting:', { success, updated, preNew, preExisting });
  };
  

  useEffect(() => {
    if (loading) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [loading]);

  // Handle pending source change with confirmation dialog
  useEffect(() => {
    if (pendingSourceChange) {
      if (hasFileData) {
        // Show confirmation dialog only if there's file data
        Swal.fire({
          title: 'Reload Required',
          text: 'To change import type, a page reload is necessary because you already have a file uploaded.',
          icon: 'info',
          confirmButtonColor: '#22c55e', // green-500
          confirmButtonText: 'Reload Now',
          showCancelButton: true,
          cancelButtonText: 'Cancel',
          allowOutsideClick: false
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.reload();
          } else {
            // User cancelled - clear the pending change
            setPendingSourceChange(null);
          }
        });
      } else {
        // No file data, just change the source directly
        setSelectedSource(pendingSourceChange);
        setPendingSourceChange(null);
      }
    }
  }, [pendingSourceChange, hasFileData]);

  return (
    <div className='bg-gray-900 min-h-screen'>
      {loading && (
        <div className="fixed inset-0 bg-hs-light-gray bg-opacity-90 flex justify-center items-center z-50">
          <div className="flex flex-col text-center">
            <img src="/Zach.png" alt='' height={150} width={150} className='animate-spin-slow hover:cursor-pointer self-center' />
            <LoadingSpinner setLoading={setLoading} toggleModal={toggleModal} selectedSource={selectedSource} className='justify-center items-center '/>
          </div>
        </div>
      )}
      <Navbar/>

      {/* Import type buttons moved into ProjectUpload for placement beneath heading */}
      
      <ProjectUpload 
        setLoading={setLoading} 
        toggleModal={toggleModal} 
        selectedSource={selectedSource} 
        user={userWithFranchisee} 
        onFileDataChange={setHasFileData}
        onStepChange={setCurrentStep}
        onChangeSource={(source) => {
          if (selectedSource !== source) {
            if (hasFileData) {
              setPendingSourceChange(source);
            } else {
              setSelectedSource(source);
            }
          }
        }}
      />

      {isModalOpen && (
        <Modal toggleModal={toggleModal} message={modalMessage} successCount={successCount} updatedCount={updatedCount} preImportNew={preImportNew} preImportExisting={preImportExisting}/>
      )}

      {isHelpOpen && (
        <GuideModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)}>
          <div className="text-white">
            <h3 className="text-lg font-semibold mb-4">Import types overview</h3>
            <ul className="space-y-3 text-sm font-normal text-gray-200">
              <li>
                <span className="font-semibold text-white">PlanHub</span>
                <span>: CSV exported from <u>PlanHub</u>.</span>
              </li>
              <li>
                <span className="font-semibold text-white">ConstructConnect</span>
                <span>: CSV exported from <u>ConstructConnect</u>.</span>
              </li>
              <li>
                <span className="font-semibold text-white">Project List</span>
                <span>: Use for sources <u>outside PlanHub/ConstructConnect</u> where each contact is associated to a specific project (rows include a Project ID and project fields).</span>
              </li>
              <li>
                <span className="font-semibold text-white">Lead List</span>
                <span>: Use when you <u>only have contacts/leads (no Projects)</u>. All leads in this upload will be <i>grouped under a single HubSpot deal that you name</i>.</span>
              </li>
            </ul>
            <div className="mt-4 text-xs text-gray-300">
              Tip: After selecting an import type, upload your file and configure any pre-filters before clicking "Filter File".
            </div>
            {/* Close handled by GuideModal's built-in Got it button */}
          </div>
        </GuideModal>
      )}

    </div>
  );
}

export default Home;
